import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {AddVideoComponent} from "../add-video/add-video.component";
import {AddImageComponent} from "../add-image/add-image.component";
import {AddTextComponent} from "../add-text/add-text.component";
import {AddAccordionComponent} from "../add-accordion/add-accordion.component";
import {AddGoogleSlidesComponent} from "../add-google-slides/add-google-slides.component";
import {ResourcesHubService} from "../../core/resources-hub.service";
import {Subject, takeUntil} from "rxjs";

@Component({
  selector: 'app-add-content',
  templateUrl: './add-content.component.html',
  styleUrls: ['./add-content.component.css']
})
export class AddContentComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() contentData: any = {
    uuid: '',
    layout: []
  };
  @Input() sectionUuid: string;
  @Output() layoutValuesChangeEmitter: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('leftResourceLayout', {read: ViewContainerRef}) leftResourceLayout!: ViewContainerRef;
  @ViewChild('middleResourceLayout', {read: ViewContainerRef}) middleResourceLayout!: ViewContainerRef;
  @ViewChild('rightResourceLayout', {read: ViewContainerRef}) rightResourceLayout!: ViewContainerRef;
  layoutMapping = {
    0: {
      id: '#leftResourceLayout',
      name: 'leftResourceLayout',
    },
    1: {
      id: '#middleResourceLayout',
      name: 'middleResourceLayout',
    },
    2: {
      id: '#rightResourceLayout',
      name: 'rightResourceLayout',
    }
  }
  contentTypeMap = {
    'video': AddVideoComponent,
    'image': AddImageComponent,
    'text': AddTextComponent,
    'accordion': AddAccordionComponent,
    'google-slide': AddGoogleSlidesComponent
  }
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(private resourceHubService: ResourcesHubService, private changeDetectorRef: ChangeDetectorRef) {
  }



  ngOnInit(): void {
    this.resourceHubService.layoutDataSubject.pipe(takeUntil(this.onDestroy)).subscribe(updatedLayoutData => {
      if (updatedLayoutData && updatedLayoutData?.sectionUuid === this.sectionUuid && this.contentData.uuid === updatedLayoutData?.contentUuid) {
        const currentLayout = this.contentData.layout[updatedLayoutData.currentComponentIndex];
        const componentIndex = updatedLayoutData.currentComponentIndex;
        let needsComponentUpdate = false;

        if (currentLayout.contentType === 'google-slide' && currentLayout.googleSlideUrl != updatedLayoutData.layoutData.googleSlideUrl)
          needsComponentUpdate = true;
        if (currentLayout.contentType === 'video' && currentLayout.videoUrl != updatedLayoutData.layoutData.videoUrl)
          needsComponentUpdate = true;
        if (currentLayout.contentType === 'image' && currentLayout.imageUrl != updatedLayoutData.layoutData.imageUrl)
          needsComponentUpdate = true;
        if (currentLayout.contentType === 'accordion' && updatedLayoutData.actionType === 'initialAccordionModuleSetup')
          needsComponentUpdate = true;

        if (needsComponentUpdate) {
          // Update contentData before recreating component so new instance gets latest data
          const updatedLayout = [...this.contentData.layout];
          updatedLayout[componentIndex] = updatedLayoutData.layoutData;
          this.contentData = {...this.contentData, layout: updatedLayout};
          this.onLayoutValueChange(updatedLayoutData?.layoutData, componentIndex);
        }

        // Content-only changes for text and accordion: update contentData and emit to store, do NOT recreate component
        if ((currentLayout.contentType === 'text' && currentLayout.content != updatedLayoutData.layoutData.content) ||
            (currentLayout.contentType === 'accordion' && updatedLayoutData.actionType === 'accordionContentUpdate')) {
          // Merge with current layout to preserve contentType, width, uuid from store
          const updatedLayout = [...this.contentData.layout];
          updatedLayout[componentIndex] = {...currentLayout, ...updatedLayoutData.layoutData};
          this.contentData = {...this.contentData, layout: updatedLayout};
          this.layoutValuesChangeEmitter.emit({
            contentUuid: this.contentData.uuid,
            sectionUuid: this.sectionUuid,
            layout: updatedLayout[componentIndex]
          });
        }
        this.resourceHubService.sendDataToParent(null)
      }
    })
  }

  ngAfterViewInit(): void {
    if (this['contentData']) {
      this.contentData.layout.map((layout, index) => {
        if (layout.contentType) {
          this.createComponentBasedOnType(layout, index);
        }
      });
      this.changeDetectorRef.detectChanges();
    }
  }

  onLayoutValueChange(event, index: number) {
    // Merge event with current layout so new component gets complete data (contentType, width, etc.)
    const updatedLayout = [...this.contentData.layout];
    updatedLayout[index] = {...updatedLayout[index], ...event};
    this.contentData = {...this.contentData, layout: updatedLayout};
    this.layoutValuesChangeEmitter.emit({
      contentUuid: this.contentData.uuid,
      sectionUuid: this.sectionUuid,
      layout: updatedLayout[index]
    })
    this.createComponentBasedOnType(updatedLayout[index], index);
  }

  createComponentBasedOnType(layout: any, index: number) {
    this.createDynamicComponent(index, this.contentTypeMap[layout.contentType]);
  }

  createDynamicComponent(index: number, component: any) {
    const viewContainerRef = this[this.layoutMapping[index].name];
    if (viewContainerRef) {
      viewContainerRef.clear();
      const componentRef = viewContainerRef.createComponent(component);
      const dynamicComponentRef = <any>componentRef.instance
      dynamicComponentRef.data = {
        contentUuid: this.contentData.uuid,
        layoutData: this.contentData.layout[index],
        sectionUuid: this.sectionUuid,
        currentComponentIndex: index
      };
    }
  }

  trackByLayout(index: number, layout: any): string {
    return layout.uuid || index.toString();
  }

  getWidth(eachLayout) {
    return {
      width: eachLayout.width === 100 ? eachLayout.width + '%' : eachLayout.width - 1 + '%'
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

}
